import { useField } from 'formik';
import { twMerge } from 'tailwind-merge';
import { useMemo, ReactNode } from 'react';
import {
  IconButton,
  Typography,
  FormControl,
  FormHelperText,
} from '@mui/material';

import { useIsMobile } from 'hooks';
import { controlError } from 'utils';

export interface ScaleRatingFieldProps {
  min: number;
  max: number;
  name: string;
  label?: string;
  header?: ReactNode;
  labelClassName?: string;
}

export const ScaleRatingField = ({
  min,
  max,
  name,
  label,
  header,
  labelClassName,
}: ScaleRatingFieldProps) => {
  const [field, meta, helpers] = useField<number | null>(name);
  const isMobile = useIsMobile();

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);

  const { width, items } = useMemo(() => {
    const items = [...Array(max - min + 1)].map((_, i) => min + i);
    const size = isMobile ? 28 : 32;
    const spacing = isMobile ? 8 : 10;
    const width = items.length * size + (items.length - 1) * spacing;
    return { width, items };
  }, [min, max, isMobile]);

  return (
    <FormControl error={hasError}>
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        {label && <Typography className={labelClassName}>{label}</Typography>}

        <div style={{ width }}>
          {header}
          <div className="flex gap-2 md:gap-2.5">
            {items.map((value) => {
              const isSelected = field.value === value;
              return (
                <IconButton
                  key={value}
                  onClick={() => helpers.setValue(value, true)}
                  className={twMerge(
                    'h-7 w-7 md:h-8 md:w-8',
                    isSelected
                      ? 'border-accent-primary-primary bg-accent-gradient-primary-primary hover:bg-accent-gradient-primary-hover active:bg-accent-gradient-primary-active'
                      : 'border-gray-active border-[1.5px]'
                  )}
                >
                  <Typography
                    className={
                      isSelected ? 'text-white-primary' : 'text-black-primary'
                    }
                  >
                    {value}
                  </Typography>
                </IconButton>
              );
            })}
          </div>
        </div>
      </div>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};
